import { forEach, map } from "underscore";

export const downloadFile = (
  content: any,
  fileName: string,
  fileType: string,
) => {
  const blob = new Blob([content], { type: fileType });
  const downloadUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = downloadUrl;
  a.download = fileName;
  a.click();
  setTimeout(() => {
    URL.revokeObjectURL(downloadUrl);
  }, 100);
};

/*
    This method reads a list of CSV files and returns an array of all unique comma separated value inside.
    BEWARE, this will not return a 2-dimensional array if the file has values spread across
    multiple line.
*/

const fileEntries = (csvData: any) => {
  let accepted: string[] = [];
  let discarded: string[] = [];
  const row = csvData.split("\n") ?? [];
  if (row.length) {
    forEach(row, (item: any) => {
      map(item.split(","), (entry: any) => {
        if (!accepted.includes(entry.trim()) && accepted.length <= 100) {
          accepted.push(entry.trim());
        } else {
          discarded.push(entry.trim());
        }
      });
    });
  }
  return { accepted: accepted, discarded: discarded };
};

export const readRecipientFiles = (files: File[]): Promise<string[]> => {
  let records: any = {};
  return new Promise((resolve, reject) => {
    try {
      forEach(files, (itr: any, index: number) => {
        if (itr) {
          const reader = new FileReader();
          reader.readAsText(itr);
          reader.onload = () => {
            const csvData: any = reader?.result ?? "";
            if (csvData.includes("<") || csvData.includes(">")) {
              reject(
                new Error(
                  "The file contains invalid characters. Unable to proceed",
                ),
              );
              return;
            }
            records = fileEntries(csvData);
            index === files.length - 1 && resolve(records);
          };
        }
      });
    } catch (error) {
      reject(new Error("Something went wrong"));
    }
  });
};

/**
 * Returns a deep copy of the object
 */
export const deepCopy = (oldObj: any) => {
  let newObj = oldObj;
  if (oldObj && typeof oldObj === "object") {
    if (oldObj instanceof Date) {
      return new Date(oldObj.getTime());
    }
    newObj =
      Object.prototype.toString.call(oldObj) === "[object Array]" ? [] : {};
    for (const i in oldObj) {
      newObj[i] = deepCopy(oldObj[i]);
    }
  }
  return newObj;
};

export const filterItems = (items: any[], searchText: string): any[] => {
  if (!items) return [];
  if (!searchText) return items;
  searchText = searchText.toLowerCase();
  return items.filter((item) => {
    let found = false;
    for (const key in item) {
      found =
        typeof item[key] !== "object" &&
        item[key].toString().toLowerCase().includes(searchText);
      if (found) break;
    }
    return found;
  });
};

export const transformObjectToArray = (value: any): any => {
  const keys = [];
  for (const key in value) {
    keys.push({ key: key, value: value[key] });
  }
  return keys;
};

export const dynamicSort = (property: any) => {
  let sortOrder = 1;

  if (property[0] === "-") {
    sortOrder = -1;
    property = property.substr(1);
  }

  return (a: any, b: any) => {
    if (sortOrder == -1) {
      return b[property].localeCompare(a[property]);
    } else {
      return a[property].localeCompare(b[property]);
    }
  };
};

export const isNumeric = (num: any) => {
  return !isNaN(num);
};

export const sanitizedUrl = (url: any) => {
  // If the URL is not a string, return a default value
  if (typeof url !== "string") {
    return "about:blank";
  }

  // Remove any control characters from the URL
  const allowedCharactersRegex = /[A-Za-z0-9\-._~:/?#[\]@!$&'()*+,;=]/g;
  url = url.replace(new RegExp(`[^${allowedCharactersRegex.source}]`, "g"), "");

  // Check if the URL has a valid scheme (http, https, ftp, etc.)
  const scheme = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.exec(url);
  if (!scheme || !["http:", "https:", "ftp:"].includes(scheme[0])) {
    return "about:blank";
  }

  // If the URL is a data or javascript URI, return a default value
  if (/^(data|javascript):/.test(url)) {
    return "about:blank";
  }

  // Return the sanitized URL
  return url;
};

export const REGEX = {
  EMAIL: /^[\w.-]+@[a-zA-Z0-9_-]+(\.[a-zA-Z]*)*$/,
  HTTPS: /(https):\/\/(\w+:?\w*@)?(\S+)(:\d+)?(\/|\/([\w#:.?+=&%@!\-/]))?/,
  ALPHANUMERIC: /^[a-zA-Z0-9]+$/,
};

export const isEmpty = (str: any) => {
  return !str || str.length === 0;
};

export const setTimeOut = async (time: number = 1000) => {
  return new Promise<void>((resolve) => {
    setTimeout(resolve, time);
  });
};

export const createNumberToString = () => {
  const ordinalSuffix = (num: any) => {
    const suffixes = {
      1: "st",
      2: "nd",
      3: "rd",
    } as any;
    const teenSuffix = "th";
    const lastDigit = num % 10;
    const lastTwoDigits = num % 100;

    return suffixes[lastDigit] || suffixes[lastTwoDigits] || teenSuffix;
  };

  return function (num: number) {
    return `${num}${ordinalSuffix(num)}`;
  };
};

export const setExportFileHeaders = (fileTypes: string): any => {
  return {
    headers:
      fileTypes === "CSV"
        ? { Accept: "text/csv; charset=utf-8" }
        : {
            "Content-Disposition": "attachment; filename=template.xlsx",
            "Content-Type":
              "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          },
    responseType: fileTypes === "CSV" ? "text" : "arraybuffer",
  };
};

export const fileStatus = {
  QUEUED: "Queued",
  PROCESSING: "Processing",
  READY: "Ready",
  DOWNLOADED: "Downloaded",
  DELETED: "Deleted",
  TERMINATED: "Terminated",
  ERROR: "ERROR",
};

export const fileStatusColor: Record<string, string> = {
  QUEUED: "bg-primary-100 text-secondary-600 ",
  READY: "bg-primary-100 text-primary-600",
  PROCESSING: "bg-yellow-400 text-yellow-800",
  TERMINATED: "bg-red-200 text-red-800",
  DOWNLOADED: "bg-green-200 text-green-800",
  ERROR: "bg-red-200 text-red-800",
};
